import { SelectProps } from "@/components/Inputs/Select/SelectProps.ts";
import { Loader } from "@/components/Loader/Loader.tsx";
import { useShadowRoot } from "@/helpers/ShadowRootContext.ts";
import { ChevronDown, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Generic component that mimics HTML <select> element because of the need for custom styles
 */
export const Select = (props: SelectProps) => {
  const { t } = useTranslation();
  const { shadowRoot } = useShadowRoot();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string>("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  const clearSelection = () => {
    setSelectedOption("");
    props.onSelect("");
  };

  const handleSetSelectedOption = useCallback(() => {
    const viewedOption = props.options.find((opt) =>
      props.optionValueKey ? opt[props.optionValueKey] === props.value : opt.id === props.value,
    );
    const value = viewedOption?.name || "";
    if (viewedOption) setSelectedOption(value);
  }, [props.options, props.optionValueKey, props.value]);

  // set initial value
  useEffect(() => {
    if (props.value) handleSetSelectedOption();
    else setSelectedOption(t("selectAccType"));
  }, [t, setSelectedOption, props.value, handleSetSelectedOption]);

  useEffect(() => {
    shadowRoot.addEventListener("mousedown", handleClickOutside as EventListener);
    return () => {
      shadowRoot.removeEventListener("mousedown", handleClickOutside as EventListener);
    };
  }, [shadowRoot]);

  const isPlaceHolderValue = selectedOption === t("selectAccType");

  const handleSelect = (option: string) => {
    props.onSelect(option);
    setIsOpen(false);
  };

  return (
    <div
      className={`selectWrap ${isOpen ? "dropdownOpen" : ""}`}
      ref={dropdownRef}
      onClick={() => setIsOpen(!isOpen)}
    >
      {props.prefixIcon}
      <div className="selectWrap__label">
        <span className="label">{t("accommodationType")}</span>
        <ChevronDown size={16} className={`selectWrap__icon ${isOpen ? "selectWrap__icon--open" : ""}`} />
      </div>
      <div className={`${isPlaceHolderValue ? "selectComponent placeholder" : "selectComponent"}`}>
        <span className="selectComponent__value">
          {props?.loading ? <Loader loading component={<div>{t("loading")}...</div>} /> : selectedOption}
        </span>
      </div>
      {isOpen && (
        <div className="selectDropdownMenu">
          {props.options?.map((option, index) => {
            const optionValue = props.optionValueKey ? option[props.optionValueKey] : option?.id;
            const isSelected = optionValue === props.value;
            return (
              <div
                key={index}
                className={isSelected ? "selectDropdownMenu__option--selected" : ""}
                onClick={() => handleSelect(optionValue)}
              >
                {option?.name}
              </div>
            );
          })}
        </div>
      )}
      {isOpen && selectedOption && !isPlaceHolderValue && (
        <div className="clearSelection">
          <X size={14} onClick={clearSelection} />
        </div>
      )}
    </div>
  );
};
